import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Radio, Spin } from 'antd';
import 'chart.js/auto';
import Chart from 'chart.js/auto';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getCurrentLanguage, translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { DefaultTimezone } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getTranslatedLocationName } from '../DeviceInventory/DeviceInventoryList';
import { getUserConfig } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const TrendChart = ({
  outdoorData = {},
  indoorData = {},
  deviceUuidToShowTrend,
  tempUnit,
  location,
  loginUserLocale,
  loginUserTimeformat,
  selectedRange,
  setSelectedRange,
  timezone,
  view,
  setViewHandler,
  indoorTrendDataLoader,
  outdoorTrendDataLoader,
  timezoneOffset,
  summaryData,
  hideAmbientLight = false,
}) => {
  const trans = useSelector(translator);
  const safeTimezone = timezone?.split(' ')[0] || DefaultTimezone;
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentUser = useSelector(getCurrentUser);
  const currentUseruuid = useMemo(() => currentUser?.useruuid, [currentUser]);
  const currentUserConfig = useSelector(getUserConfig(currentUseruuid));
  const currentLanguage = useSelector(getCurrentLanguage);
  const translatedLocation = useMemo(
    () => getTranslatedLocationName(location, currentUserConfig, currentLanguage),
    [location, currentUserConfig, currentLanguage]
  );
  const isMountedRef = useRef(true);
  const processingRef = useRef(false);

  useEffect(() => {
    const timeZonePlugin = {
      id: 'timeZonePlugin',
      afterDatasetsDraw: (chart, args, options) => {
        const { ctx, chartArea } = chart;
        const xAxis = chart.scales.x;
        const xAxisBottom = xAxis ? xAxis.bottom : chartArea.bottom;
        ctx.save();
        ctx.font = options.font || '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = options.color || 'black';
        const text = options.text || '';
        ctx.fillText(text, (chartArea.left + chartArea.right) / 2, xAxisBottom + 20);
        ctx.restore();
      },
    };

    Chart.register(timeZonePlugin);

    return () => {
      Chart.unregister(timeZonePlugin);
    };
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;

    if (!indoorTrendDataLoader && !outdoorTrendDataLoader) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [indoorTrendDataLoader, outdoorTrendDataLoader]);

  useEffect(() => {
    if (!isMountedRef.current || processingRef.current) return;

    processingRef.current = true;

    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current) {
        processingRef.current = false;
        return;
      }

      try {
        const dataMap = {};

        // Process outdoor data
        Object.keys(outdoorData).forEach((date) => {
          const outdoor = outdoorData[date].outdoor?.environData;
          if (!outdoor) return;

          Object.keys(outdoor.hourlyTemp).forEach((hour) => {
            const dateTime = moment(date).hour(hour).format('YYYY-MM-DD HH:mm');

            if (!dataMap[dateTime]) {
              dataMap[dateTime] = { dateTime };
            }
            dataMap[dateTime].outdoorTemperature = outdoor.hourlyTemp[hour];
            dataMap[dateTime].outdoorHumidity = outdoor.hourlyHumidity[hour];
          });
        });

        // Process indoor data
        Object.keys(indoorData).forEach((date) => {
          const indoor = indoorData[date][deviceUuidToShowTrend];
          if (!indoor) return;

          const environData = indoor.environData;
          Object.keys(environData.hourlyTemp).forEach((hour) => {
            const dateTime = moment(date).hour(hour).format('YYYY-MM-DD HH:mm');
            if (!dataMap[dateTime]) {
              dataMap[dateTime] = { dateTime };
            }
            dataMap[dateTime].indoorTemperature = environData.hourlyTemp[hour];
            dataMap[dateTime].indoorHumidity = environData.hourlyHumidity[hour];
          });
        });

        // Sort data by dateTime
        const sortedData = Object.values(dataMap).sort(
          (a, b) => new Date(a.dateTime) - new Date(b.dateTime)
        );

        const createDataset = (label, dataArr, borderColor, borderDash) => ({
          label,
          data: dataArr.map((temp) => (temp !== undefined ? temp : null)),
          borderColor,
          borderDash,
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          spanGaps: false,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointStyle: 'circle',
        });

        const now = dayjs().utcOffset(timezoneOffset);
        const today = now.startOf('day');

        let cutoff;
        let endLimit;

        if (selectedRange === 'today') {
          cutoff = now.subtract(24, 'hours');
          endLimit = now;
        } else {
          const rangeDays = selectedRange === 'weekly' ? 7 : 30;
          cutoff = today.subtract(rangeDays, 'day');
        }

        const filtered = sortedData.filter((entry) => {
          const entryDayjs = dayjs(entry.dateTime).utcOffset(timezoneOffset);
          if (selectedRange === 'today') {
            return entryDayjs.isAfter(cutoff) && entryDayjs.isBefore(endLimit);
          } else {
            return entryDayjs.isAfter(cutoff);
          }
        });

        let finalData = filtered;
        if (selectedRange === 'monthly') {
          const allDatesMap = {};
          const rangeDays = 30;
          const monthlyCutoff = today.subtract(rangeDays, 'day');

          for (let i = 0; i < rangeDays; i++) {
            const currentDate = monthlyCutoff.add(i + 1, 'day');
            const dayKey = currentDate.format('YYYY-MM-DD');

            const existingDataForDay = filtered.filter(
              (entry) => dayjs(entry.dateTime).format('YYYY-MM-DD') === dayKey
            );

            if (existingDataForDay.length > 0) {
              existingDataForDay.forEach((entry) => {
                allDatesMap[entry.dateTime] = entry;
              });
            } else {
              const placeholderDateTime = currentDate.format('YYYY-MM-DD HH:mm');
              allDatesMap[placeholderDateTime] = {
                dateTime: placeholderDateTime,
                indoorTemperature: null,
                outdoorTemperature: null,
                indoorHumidity: null,
                outdoorHumidity: null,
              };
            }
          }

          finalData = Object.values(allDatesMap).sort(
            (a, b) => new Date(a.dateTime) - new Date(b.dateTime)
          );
        }

        const labels = finalData.map((entry) => dayjs.tz(entry.dateTime, safeTimezone).valueOf());
        const rawTimestamps = finalData.map((entry) => entry.dateTime);

        const chartMappedData = {
          labels,
          rawTimestamps,
          datasets:
            view === 'temperature'
              ? [
                  createDataset(
                    trans('OUTDOORTEMPERATURE'),
                    finalData.map((e) => e.outdoorTemperature),
                    'darkgray',
                    [5, 5]
                  ),
                  createDataset(
                    trans('INDOORTEMPERATURE'),
                    finalData.map((e) => e.indoorTemperature),
                    'orange'
                  ),
                ]
              : [
                  createDataset(
                    trans('OUTDOORHUMIDITY'),
                    finalData.map((e) => e.outdoorHumidity),
                    'darkgray',
                    [5, 5]
                  ),
                  createDataset(
                    trans('INDOORHUMIDITY'),
                    finalData.map((e) => e.indoorHumidity),
                    'orange'
                  ),
                ],
        };

        if (isMountedRef.current) {
          setChartData(chartMappedData);
        }
      } catch (error) {
        console.error('Error processing chart data:', error);
      } finally {
        processingRef.current = false;
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      processingRef.current = false;
    };
  }, [indoorData, outdoorData, selectedRange, tempUnit, view, deviceUuidToShowTrend, safeTimezone]);

  // Chart options
  const chartOptions = useMemo(() => {
    if (!chartData) return {};

    const getDateFormatByLocation = (userTimezone) => {
      const timeZoneArray = userTimezone.toUpperCase().split('/');
      const timeZone = timeZoneArray[0];
      const timeZoneArea = timeZoneArray[1];
      if (timeZone === 'AMERICA' || (timeZone === 'PACIFIC' && timeZoneArea === 'HONOLULU')) {
        return 'MMM D';
      }
      return 'D MMM';
    };

    return {
      layout: {
        padding: { bottom: 30 },
      },
      scales: {
        x: {
          type: 'time',
          bounds: selectedRange === 'weekly' || selectedRange === 'monthly' ? 'data' : 'ticks',
          offset: selectedRange === 'weekly' || selectedRange === 'monthly' ? false : true,
          min:
            (selectedRange === 'weekly' || selectedRange === 'monthly') &&
            chartData.labels?.length > 0
              ? chartData.labels[0]
              : undefined,
          max:
            (selectedRange === 'weekly' || selectedRange === 'monthly') &&
            chartData.labels?.length > 0
              ? chartData.labels[chartData.labels.length - 1]
              : undefined,
          time: {
            unit: selectedRange === 'today' ? 'hour' : 'day',
            displayFormats: {
              hour: selectedRange === 'today' ? 'MMM D HH:mm' : 'HH:mm',
              day: getDateFormatByLocation(safeTimezone),
            },
          },
          ticks: {
            source: 'data',
            autoSkip: false,
            callback: function (value) {
              const d = new Date(value);

              if (selectedRange === 'today') {
                const hour = Number(
                  d.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    hourCycle: 'h23',
                    timeZone: safeTimezone,
                  })
                );

                if (hour % 2 !== 0) {
                  return null;
                }

                return d.toLocaleTimeString(loginUserLocale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: safeTimezone,
                });
              }

              if (selectedRange === 'monthly') {
                const dayOfMonth = Number(
                  d.toLocaleDateString('en-US', { day: 'numeric', timeZone: safeTimezone })
                );

                if ((dayOfMonth - 1) % 4 !== 0) {
                  return null;
                }

                return d.toLocaleDateString(loginUserLocale, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  timeZone: safeTimezone,
                });
              }

              /* -------- WEEKLY -------- */
              return d.toLocaleDateString(loginUserLocale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: safeTimezone,
              });
            },
            maxTicksLimit: selectedRange === 'monthly' ? undefined : 10,
            maxRotation: 45,
            minRotation: 0,
            includeBounds: true,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: true,
          callbacks: {
            title: function (context) {
              const timeString = chartData.rawTimestamps?.[context[0].dataIndex];
              const formattedDate = timeString
                ? dayjs.tz(timeString, safeTimezone).format(getDateFormatByLocation(safeTimezone))
                : 'N/A';
              return formattedDate;
            },
            label: function (context) {
              const value = context.raw !== null ? context.raw.toFixed(1) : 'N/A';
              const timeString = chartData.rawTimestamps?.[context.dataIndex];
              const formattedDate = timeString
                ? dayjs.tz(timeString, safeTimezone).format(getDateFormatByLocation(safeTimezone))
                : 'N/A';
              const formattedTime = timeString
                ? dayjs
                    .tz(timeString, safeTimezone)
                    .format(loginUserTimeformat?.is24hoursformat ? 'HH:mm' : 'h:mm A')
                : 'N/A';

              if (context.dataset.label.includes('Humidity')) {
                return [`${context.dataset.label}: ${value}%`, `${trans('TIME')} ${formattedTime}`];
              } else {
                return [
                  `${context.dataset.label}: ${value}°${tempUnit}`,
                  `${trans('TIME')} ${formattedTime}`,
                ];
              }
            },
          },
        },
        legend: {
          display: true,
        },
        datalabels: {
          display: false,
        },
        timeZonePlugin: {
          text: `${trans('TIMEZONE')}: ${safeTimezone} (${timezoneOffset})`,
          font: '14px Arial',
          color: '#666',
        },
      },
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 6,
        },
      },
      animation: false,
    };
  }, [chartData, loginUserTimeformat, tempUnit, selectedRange, safeTimezone]);

  // Stable handler functions
  const handleRangeChange = useCallback(
    (range) => {
      if (isMountedRef.current && range !== selectedRange) {
        setSelectedRange(range);
      }
    },
    [selectedRange, setSelectedRange]
  );

  const handleViewChange = useCallback(
    (newView) => {
      if (isMountedRef.current && newView !== view) {
        setViewHandler(newView);
      }
    },
    [view, setViewHandler]
  );

  return (
    <div className="trend-chart-container">
      <div className="graph-title-container">
        <div className="environmental-chart-selectors-container">
          <div className="environmental-chart-view-options-container">
            <div className="environmental-chart-view-block trend-chart">
              <div
                className={`environmental-chart-view-option ${view === 'temperature' ? 'selected-option' : ''}`}
                onClick={() => handleViewChange('temperature')}
              >
                <span
                  className={`environmental-chart-view-option-label ${view === 'temperature' ? 'selected' : ''}`}
                >
                  {trans('TEMPERATURE')}
                </span>
              </div>
              <div
                className={`environmental-chart-view-option ${view === 'humidity' ? 'selected-option' : ''}`}
                onClick={() => handleViewChange('humidity')}
              >
                <span
                  className={`environmental-chart-view-option-label ${view === 'humidity' ? 'selected' : ''}`}
                >
                  {trans('HUMIDITY')}
                </span>
              </div>
            </div>
          </div>

          <div className="environmental-chart-view-options-container">
            <div className="environmental-chart-view-block time trend-chart">
              <div
                className={`environmental-chart-view-option ${selectedRange === 'today' ? 'selected-option' : ''}`}
                onClick={() => handleRangeChange('today')}
              >
                <span
                  className={`environmental-chart-view-option-label ${selectedRange === 'today' ? 'selected' : ''}`}
                >
                  {trans('DAY')}
                </span>
              </div>
              <div
                className={`environmental-chart-view-option ${selectedRange === 'weekly' ? 'selected-option' : ''}`}
                onClick={() => handleRangeChange('weekly')}
              >
                <span
                  className={`environmental-chart-view-option-label ${selectedRange === 'weekly' ? 'selected' : ''}`}
                >
                  {trans('WEEK')}
                </span>
              </div>
              <div
                className={`environmental-chart-view-option ${selectedRange === 'monthly' ? 'selected-option' : ''}`}
                onClick={() => handleRangeChange('monthly')}
              >
                <span
                  className={`environmental-chart-view-option-label ${selectedRange === 'monthly' ? 'selected' : ''}`}
                >
                  {trans('MONTH')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="env-modal-insight-chart-summary-row">
        <div className="env-modal-insight-chart-summary-item">
          <div className="env-modal-insight-chart-summary-item-label">
            <div className="env-modal-insight-chart-label" />
          </div>
          <div className="env-modal-insight-chart-summary-item-value-header">
            <div className="env-modal-insight-chart-value low-header">{trans('LOW')}</div>
            <div className="env-modal-insight-chart-value high-header">{trans('HIGH')}</div>
          </div>
        </div>
        <div className="env-modal-insight-chart-summary-item">
          <div className="env-modal-insight-chart-summary-item-label">
            <div className="env-modal-insight-chart-label">{trans('TEMPERATURE')}</div>
          </div>
          <div className="env-modal-insight-chart-summary-item-value">
            <div className="env-modal-insight-chart-value_low">
              {' '}
              {Math.round(summaryData?.temp?.low)}° {tempUnit}
            </div>
            <div className="env-modal-insight-chart-value_high">
              {' '}
              {Math.round(summaryData?.temp?.high)}° {tempUnit}
            </div>
          </div>
        </div>
        <div className="env-modal-insight-chart-summary-item">
          <div className="env-modal-insight-chart-summary-item-label">
            <div className="env-modal-insight-chart-label">{trans('HUMIDITY')}</div>
          </div>
          <div className="env-modal-insight-chart-summary-item-value">
            <div className="env-modal-insight-chart-value_low">
              {' '}
              {Math.round(summaryData?.humidity?.low)} {'%'}
            </div>
            <div className="env-modal-insight-chart-value_high">
              {' '}
              {Math.round(summaryData?.humidity?.high)} {'%'}
            </div>
          </div>
        </div>
        {!hideAmbientLight && (
          <div className="env-modal-insight-chart-summary-item">
            <div className="env-modal-insight-chart-summary-item-label">
              <div className="env-modal-insight-chart-label">{trans('AMBIENTLIGHT')}</div>
            </div>
            <div className="env-modal-insight-chart-summary-item-value">
              <div className="env-modal-insight-chart-value_low">
                {' '}
                {Math.round(summaryData?.light?.low)}{' '}
              </div>
              <div className="env-modal-insight-chart-value_high">
                {' '}
                {Math.round(summaryData?.light?.high)}{' '}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="trend-chart-line-container">
        {loading ? (
          <Spin spinning={loading} className="trend-chart-loading-spinner" />
        ) : chartData ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <p className="trend-chart-loading-text">{trans('LOADING')}</p>
        )}
      </div>
    </div>
  );
};

export default TrendChart;
